import {
  Component,
  Input,
  ViewEncapsulation,
  Output,
  EventEmitter,
  HostListener,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  DoCheck
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType, Chart, registerables } from 'chart.js';

// Register all Chart.js components
Chart.register(...registerables);

@Component({
  standalone: true,
  selector: 'app-bar-line-combo-chart',
  imports: [CommonModule, BaseChartDirective],
  templateUrl: './bar-line-combo-chart.component.html',
  styleUrl: './bar-line-combo-chart.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class BarLineComboChartComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges, DoCheck {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  @ViewChild(BaseChartDirective) chart!: BaseChartDirective;
  @Input() view: [number, number] = [700, 300];
  @Input() scheme: any = null; // For compatibility - not used in Chart.js
  @Input() colorSchemeLine: any = null; // For compatibility - not used in Chart.js
  @Input() results: any[] = [];
  @Input() groupedBarData: any[] = []; // Support for grouped bar data format [{name: "Brand", series: [{name: "Metric", value: 85}]}]
  @Input() lineChart: any[] = [];
  @Input() barBrandName: string = 'Bar Chart';
  @Input() animations: boolean = true;
  @Input() tooltipDisabled: boolean = false;
  @Input() yAxisTickFormatting: any = null; // For compatibility - not used in Chart.js
  @Input() yLeftAxisScaleFactor: any = null; // For compatibility - not used in Chart.js
  @Input() yRightAxisScaleFactor: any = null; // For compatibility - not used in Chart.js
  @Input() yRightAxisTickFormatting: any = null; // For compatibility - not used in Chart.js
  @Input() gradient: boolean = false;
  @Input() xAxis: boolean = true;
  @Input() yAxis: boolean = true;
  @Input() legend: boolean = true;
  @Input() legendTitle: string = 'Legend';
  @Input() legendPosition: string = 'right';
  @Input() showGridLines: boolean = true;
  @Input() showXAxisLabel: boolean = true;
  @Input() showYAxisLabel: boolean = true;
  @Input() showRightYAxisLabel: boolean = true;
  @Input() xAxisLabel: string = '';
  @Input() yAxisLabel: string = '';
  @Input() yAxisLabelRight: string = '';
  @Input() noBarWhenZero: boolean = true;
  @Input() wrapTicks: boolean = false;
  @Input() rightSided: boolean = false; // If true, bars will be horizontal (indexAxis: 'y')
  @Input() yAxisStartAtZero: boolean = true; // If false, y-axis starts at minimum data value
  @Input() showPoints: boolean = true; // If false, hide data points on line
  @Input() dateGapInterval: number = 0; // If > 0, show only every Nth label (e.g., 7 for weekly)
  @Input() lineColor: string = ''; // Custom line color (if empty, uses default colors)

  @Output() select: EventEmitter<any> = new EventEmitter();
  @Output() activate: EventEmitter<any> = new EventEmitter();
  @Output() deactivate: EventEmitter<any> = new EventEmitter();

  // Chart.js Combo Chart Configuration
  public comboChartData: ChartConfiguration['data'] = {
    labels: [],
    datasets: []
  };

  public comboChartType: ChartType = 'bar';
  
  // Store calculated y-axis min/max for custom y-axis range
  private yAxisMin: number | undefined;
  private yAxisMax: number | undefined;
  
  // Get chart options with optimized hover animations
  get comboChartOptions(): ChartConfiguration['options'] {
    const baseOptions: ChartConfiguration['options'] = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false, // Completely disable animations for instant rendering
      interaction: {
        intersect: false,
        mode: 'index'
      },
      scales: {
        y: { 
          beginAtZero: this.yAxisStartAtZero,
          display: this.yAxis,
          min: this.yAxisStartAtZero ? undefined : this.yAxisMin,
          max: this.yAxisStartAtZero ? undefined : this.yAxisMax,
          ticks: this.yAxisStartAtZero ? {} : {
            stepSize: 2 // Show values in increments of 2 when not starting at 0
          }
        },
        x: {
          display: this.xAxis,
          ticks: this.dateGapInterval > 0 ? {
            // Show only every Nth label (e.g., every 7th for weekly)
            callback: (value: any, index: number, ticks: any[]): string | number | null | undefined => {
              const labels = this.comboChartData.labels || [];
              if (labels.length === 0 || index >= labels.length) return '';
              
              // Always show first label (index 0)
              if (index === 0) return labels[0] as string;
              
              // Always show last label
              if (index === labels.length - 1) return labels[index] as string;
              
              // Show every Nth label based on dateGapInterval
              if (index % this.dateGapInterval === 0) return labels[index] as string;
              
              // Hide other labels
              return '';
            },
            maxRotation: 0,
            minRotation: 0
          } : {}
        }
      },
      plugins: {
        legend: {
          display: false // We'll use custom legend
        },
        tooltip: {
          enabled: !this.tooltipDisabled,
          animation: {
            duration: 0 // No animation delay for instant tooltip
          },
          position: 'nearest',
          intersect: false,
          mode: 'index'
        }
      },
      elements: {
        bar: {
          hoverBorderWidth: 2,
          hoverBorderColor: 'rgba(1, 87, 155, 1)',
          hoverBackgroundColor: 'rgba(1, 87, 155, 0.8)',
          // No animations on hover state change
          borderWidth: 1
        },
        point: {
          radius: this.showPoints ? 4 : 0, // Hide points if showPoints is false
          hoverRadius: this.showPoints ? 6 : 4, // Show point only on hover if points are hidden
          hoverBorderWidth: 2,
          hoverBorderColor: 'rgba(255, 255, 255, 1)'
        },
        line: {
          borderWidth: 2,
          tension: 0.1
        }
      },
      // Optimize hover performance
      onHover: (event: any, active: any) => {
        // Handle hover events immediately without delay
        if (active && active.length > 0) {
          this.onActivate(active);
        } else {
          this.onDeactivate(event);
        }
      }
    };

    // If rightSided is true, make bars horizontal
    if (this.rightSided && baseOptions.scales) {
      baseOptions.indexAxis = 'y';
      // Swap axis labels for horizontal bars
      if (baseOptions.scales['x']) {
        (baseOptions.scales['x'] as any).title = {
          display: this.showXAxisLabel,
          text: this.xAxisLabel || 'Score'
        };
      }
      if (baseOptions.scales['y']) {
        (baseOptions.scales['y'] as any).title = {
          display: this.showYAxisLabel,
          text: this.yAxisLabel || 'Categories'
        };
      }
    } else {
      // Vertical bars - normal configuration
      if (baseOptions.scales && baseOptions.scales['x']) {
        (baseOptions.scales['x'] as any).title = {
          display: this.showXAxisLabel,
          text: this.xAxisLabel || ''
        };
      }
      if (baseOptions.scales && baseOptions.scales['y']) {
        (baseOptions.scales['y'] as any).title = {
          display: this.showYAxisLabel,
          text: this.yAxisLabel || ''
        };
      }
    }

    return baseOptions;
  }

  // Chart.js specific methods
  private lastDataSignature: string = '';

  ngDoCheck(): void {
    const currentSig = this.buildDataSignature();
    if (currentSig !== this.lastDataSignature) {
      this.lastDataSignature = currentSig;
      this.updateChartData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['results'] || changes['groupedBarData'] || changes['lineChart'] || changes['rightSided'] ||
        changes['yAxisStartAtZero'] || changes['showPoints'] || changes['dateGapInterval'] || changes['lineColor']) {
      this.lastDataSignature = this.buildDataSignature();
      this.updateChartData();
    }
  }

  ngOnInit(): void {
    this.updateChartData();
  }

  ngAfterViewInit(): void {
    this.updateChartData();
    window.addEventListener('resize', this.onResize.bind(this));
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize.bind(this));
  }

  private onResize(): void {
    setTimeout(() => {
      if (this.chart && this.chart.chart) {
        // Force chart to resize properly
        this.chart.chart.resize();
        this.chart.update();
      }
    }, 100);
  }

  private updateChartData(): void {
    // Transform groupedBarData to flat results format if provided
    let flatResults = this.results;
    let brandName = this.barBrandName;
    
    if (this.groupedBarData && this.groupedBarData.length > 0) {
      // Transform grouped format [{name: "Brand", series: [{name: "Metric", value: 85}]}]
      // to flat format [{name: "Metric", value: 85}]
      const firstGroup = this.groupedBarData[0];
      if (firstGroup && firstGroup.series && Array.isArray(firstGroup.series)) {
        flatResults = firstGroup.series;
        brandName = firstGroup.name || this.barBrandName;
      }
    }
    
    // Extract labels - prioritize from results, fallback to lineChart if results is empty
    let labels: string[] = [];
    
    if (flatResults && flatResults.length > 0) {
      // Extract labels from results
      labels = flatResults.map(item => item.name);
    } else if (this.lineChart && this.lineChart.length > 0) {
      // Extract labels from first lineChart series if results is empty
      const firstLineData = this.lineChart[0];
      if (firstLineData && firstLineData.series && Array.isArray(firstLineData.series) && firstLineData.series.length > 0) {
        labels = firstLineData.series.map((item: any) => item.name);
      }
    }
    
    // If no labels found, return empty chart
    if (labels.length === 0) {
      this.comboChartData = { labels: [], datasets: [] };
      if (this.chart && this.chart.chart) {
        this.chart.update('none'); // Update without animation
      }
      return;
    }
    
    // Prepare datasets
    const datasets: any[] = [];

    // Add bar chart dataset (primary data) - only if we have results
    if (flatResults && flatResults.length > 0) {
      datasets.push({
        label: brandName,
        data: flatResults.map(item => item.value),
        backgroundColor: 'rgba(1, 87, 155, 0.6)',
        borderColor: 'rgba(1, 87, 155, 1)',
        borderWidth: 1,
        type: 'bar'
      });
    }

    // Add line chart datasets (comparison data)
    if (this.lineChart && this.lineChart.length > 0) {
      // Use custom line color if provided, otherwise use default colors
      const defaultLineColors = ['#7aa3e5', '#a8385d', '#00bfa5', '#ff9800', '#4caf50'];
      const lineColors = this.lineColor ? [this.lineColor] : defaultLineColors;
      
      this.lineChart.forEach((lineData, index) => {
        if (lineData.series && lineData.series.length > 0) {
          // Map line data values to match labels order
          const lineValues = labels.map(label => {
            const seriesItem = lineData.series.find((item: any) => item.name === label);
            return seriesItem ? seriesItem.value : null;
          });
          
          datasets.push({
            label: lineData.name,
            data: lineValues,
            borderColor: lineColors[index % lineColors.length],
            backgroundColor: lineColors[index % lineColors.length],
            type: 'line',
            tension: 0.1,
            fill: false,
            pointRadius: this.showPoints ? 4 : 0, // Use showPoints input
            pointHoverRadius: this.showPoints ? 6 : 4, // Show point only on hover if points are hidden
          });
        }
      });
    }
    
    // Calculate min/max for y-axis if not starting at 0
    this.yAxisMin = undefined;
    this.yAxisMax = undefined;
    
    if (!this.yAxisStartAtZero && datasets.length > 0) {
      const allValues: number[] = [];
      datasets.forEach(dataset => {
        if (Array.isArray(dataset.data)) {
          dataset.data.forEach((val: any) => {
            if (val !== null && val !== undefined && !isNaN(val)) {
              allValues.push(Number(val));
            }
          });
        }
      });
      
      if (allValues.length > 0) {
        const minValue = Math.min(...allValues);
        const maxValue = Math.max(...allValues);
        const padding = (maxValue - minValue) * 0.1; // 10% padding
        
        // Store calculated min/max for use in getter
        this.yAxisMin = Math.max(0, minValue - padding);
        this.yAxisMax = maxValue + padding;
      }
    }

    this.comboChartData = {
      labels: labels,
      datasets: datasets
    };

    // Update chart if it exists - use 'none' mode to avoid animations
    if (this.chart && this.chart.chart) {
      // Use update mode 'none' for instant rendering
      this.chart.update('none');
    }
  }

  private buildDataSignature(): string {
    try {
      // Use groupedBarData if provided, otherwise use results
      const dataToUse = (this.groupedBarData && this.groupedBarData.length > 0) 
        ? (this.groupedBarData[0]?.series || [])
        : this.results;
      
      const r = Array.isArray(dataToUse)
        ? dataToUse.map((it: any) => [it?.name, it?.value])
        : [];
      const l = Array.isArray(this.lineChart)
        ? this.lineChart.map((ld: any) => ({
            n: ld?.name,
            s: Array.isArray(ld?.series)
              ? ld.series.map((s: any) => [s?.name, s?.value])
              : []
          }))
        : [];
      return JSON.stringify({ r, l });
    } catch {
      return Math.random().toString();
    }
  }

  // Event handlers for Chart.js
  onChartSelect(event: any): void {
    this.select.emit(event);
  }

  onChartActivate(event: any): void {
    this.activate.emit(event);
  }

  onChartDeactivate(event: any): void {
    this.deactivate.emit(event);
  }

  // Legacy methods for compatibility
  onSelect(event: any): void {
    this.select.emit(event);
  }

  onActivate(event: any): void {
    this.activate.emit(event);
  }

  onDeactivate(event: any): void {
    this.deactivate.emit(event);
  }
}
